/**
 * /online-camera — «developing» a shot: crop to the frame the person saw,
 * mirror if asked, the same look as the preview, the date imprint, and the
 * encode to the chosen format.
 *
 * The look must be the SAME picture in the file as on the screen. The preview
 * wears a CSS `filter` on the <video> plus colour washes with mix-blend-mode;
 * here the file gets the same CSS string through ctx.filter where the canvas
 * has it (Chrome, Edge, Firefox — the browser's own implementation, so the two
 * cannot drift), and the Filter Effects matrices from core.js where it does
 * not (Safari). The washes use canvas composite modes, which are defined by
 * the same W3C compositing formulas as mix-blend-mode.
 */
import * as C from './core.js';

/** Canvas area ceiling: iOS Safari draws NOTHING into a canvas over ~16.7 Mpx. */
export const MAX_AREA = 16777216;

let _ctxFilterOk = null;
/**
 * Does ctx.filter actually filter? Assigning it on an engine without the
 * property just creates an expando that reads back the same string, so the
 * answer has to come from pixels: a red pixel through grayscale(1) must come
 * out grey.
 */
export function ctxFilterWorks() {
    if (_ctxFilterOk !== null) return _ctxFilterOk;
    try {
        const a = document.createElement('canvas'); a.width = a.height = 1;
        const ax = a.getContext('2d'); ax.fillStyle = '#ff0000'; ax.fillRect(0, 0, 1, 1);
        const b = document.createElement('canvas'); b.width = b.height = 1;
        const bx = b.getContext('2d', { willReadFrequently: true });
        bx.filter = 'grayscale(1)';
        bx.drawImage(a, 0, 0);
        const d = bx.getImageData(0, 0, 1, 1).data;
        _ctxFilterOk = Math.abs(d[0] - d[1]) < 8 && d[0] < 200;
    } catch (_) { _ctxFilterOk = false; }
    return _ctxFilterOk;
}
/** Test hook: force the matrix path (Safari's) or the canvas-filter path. */
export function _forceCtxFilter(v) { _ctxFilterOk = v == null ? null : !!v; }

/**
 * @param {CanvasImageSource} src  the captured picture
 * @param {number} srcW/srcH       its pixel size
 * @param {object} o  {streamW, streamH, aspect, zoom, mirror, filter, date(Date|null), seed}
 * @returns {HTMLCanvasElement}
 */
export function develop(src, srcW, srcH, o) {
    const rect = C.mapCrop(o.streamW || srcW, o.streamH || srcH, srcW, srcH, o.aspect || 'full', o.zoom || 1);
    const fit = C.fitArea(rect.w, rect.h, MAX_AREA);
    const W = fit.w, H = fit.h;
    const cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    const look = C.FILTERS[o.filter] || C.FILTERS.none;
    const useCtxFilter = look.ops.length && ctxFilterWorks();
    const x = cv.getContext('2d', { willReadFrequently: !!(look.ops.length && !useCtxFilter) || !!look.grain });
    x.imageSmoothingQuality = 'high';
    x.save();
    if (o.mirror) { x.translate(W, 0); x.scale(-1, 1); }
    if (useCtxFilter) x.filter = C.cssFilter(o.filter);
    x.drawImage(src, rect.x, rect.y, rect.w, rect.h, 0, 0, W, H);
    x.restore();
    if (look.ops.length && !useCtxFilter) {
        // rows in bands: one getImageData of 12 Mpx is 48 MB at once
        const band = Math.max(1, Math.floor(2000000 / W));
        for (let y = 0; y < H; y += band) {
            const h = Math.min(band, H - y);
            const id = x.getImageData(0, y, W, h);
            C.applyOps(id.data, look.ops);
            x.putImageData(id, 0, y);
        }
    }
    paintLayers(x, W, H, look);
    if (look.grain) {
        const band = Math.max(1, Math.floor(2000000 / W));
        let seed = o.seed || 7;
        for (let y = 0; y < H; y += band) {
            const h = Math.min(band, H - y);
            const id = x.getImageData(0, y, W, h);
            C.applyGrain(id.data, look.grain, seed++);
            x.putImageData(id, 0, y);
        }
    }
    if (o.date) drawImprint(x, W, H, o.date);
    return cv;
}

/** Colour washes and vignette — the file-side twin of the preview's overlay divs. */
export function paintLayers(x, W, H, look) {
    if (look.layers) {
        for (const [color, mode] of look.layers) {
            x.save();
            x.globalCompositeOperation = mode;
            x.fillStyle = color;
            x.fillRect(0, 0, W, H);
            x.restore();
        }
    }
    if (look.vignette > 0) {
        // CSS `radial-gradient(ellipse at center, …)` = farthest-corner ellipse:
        // radii (W/2)·√2 and (H/2)·√2. A circle in a y-scaled space is that ellipse.
        x.save();
        x.translate(W / 2, H / 2);
        x.scale(1, H / W);
        const R = (W / 2) * Math.SQRT2;
        const g = x.createRadialGradient(0, 0, 0, 0, 0, R);
        g.addColorStop(C.VIGNETTE_INNER, 'rgba(0,0,0,0)');
        g.addColorStop(1, 'rgba(0,0,0,' + look.vignette + ')');
        x.fillStyle = g;
        x.fillRect(-W / 2, -W / 2, W, W);     // in the scaled space this is exactly the canvas
        x.restore();
    }
}

/** The orange seven-segment-looking date of a film compact, bottom right. */
export function drawImprint(x, W, H, d) {
    const text = C.dateImprint(d);
    const size = Math.max(14, Math.round(Math.min(W, H) * 0.045));
    x.save();
    x.font = '700 ' + size + 'px "Courier New", ui-monospace, monospace';
    x.textAlign = 'right';
    x.textBaseline = 'alphabetic';
    const px = W - Math.round(size * 1.2), py = H - Math.round(size * 0.9);
    x.shadowColor = 'rgba(255,120,0,0.85)';
    x.shadowBlur = size * 0.35;
    x.fillStyle = '#ffb347';
    x.fillText(text, px, py);
    x.shadowBlur = 0;
    x.fillStyle = 'rgba(255,215,160,0.9)';
    x.fillText(text, px, py);
    x.restore();
}

let _webpOk = null;
export function webpEncodes() {
    if (_webpOk !== null) return _webpOk;
    try {
        const c = document.createElement('canvas'); c.width = c.height = 2;
        _webpOk = c.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } catch (_) { _webpOk = false; }
    return _webpOk;
}

/**
 * Encode. Returns {blob, fmt} where fmt is what the browser ACTUALLY wrote:
 * toBlob silently answers PNG for a type it cannot encode (Safari and WebP).
 */
export function encode(cv, fmt, quality) {
    const mime = C.FORMAT_MIME[fmt] || 'image/jpeg';
    const q = fmt === 'png' ? undefined : Math.min(1, Math.max(0.3, quality || 0.92));
    return new Promise((resolve, reject) => {
        try {
            cv.toBlob((b) => {
                if (!b) { reject(new Error('encode-failed')); return; }
                const real = b.type === 'image/webp' ? 'webp' : b.type === 'image/png' ? 'png' : 'jpeg';
                resolve({ blob: b, fmt: real });
            }, mime, q);
        } catch (e) { reject(e); }
    });
}

/** A small JPEG for the gallery grid (the file stays full size). */
export function thumbnail(src, sw, sh, max) {
    const m = max || 320;
    const s = Math.min(1, m / Math.max(sw, sh));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(sw * s)); c.height = Math.max(1, Math.round(sh * s));
    const x = c.getContext('2d');
    x.imageSmoothingQuality = 'high';
    x.drawImage(src, 0, 0, c.width, c.height);
    return new Promise((resolve) => c.toBlob((b) => resolve(b), 'image/jpeg', 0.8));
}

/** Draw `src` to cover a w×h box (centre crop), optionally mirrored. */
export function drawCover(x, src, sw, sh, dx, dy, w, h) {
    const r = C.centerCrop(sw, sh, w, h);
    x.drawImage(src, r.x, r.y, r.w, r.h, dx, dy, w, h);
}
